import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useTranslate} from '@computerrock/formation-i18n';
import {useRouteUnmountEffect, withRouter} from '@computerrock/formation-router';
import {apmContractPartnerAssignmentChannelTypes, efServiceAssignmentTypes} from '@ace-de/eua-entity-types';
import {useStyles, ContentBlock, ContentItem} from '@ace-de/ui-components';
import * as userProfileSelectors from '../user-profiles/userProfileSelectors';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import * as serviceAssignmentSelectors from '../service-assignments/serviceAssignmentSelectors';
import * as serviceAssignmentActionTypes from '../service-assignments/serviceAssignmentActionTypes';
import CommissioningBasicDataPanel from '../service-assignments/ui-elements/CommissioningBasicDataPanel';
import CostCoverageCostCoveragePanel from './ui-elements/CostCoverageCostCoveragePanel';
import CostCoverageDetailsPanel from './ui-elements/CostCoverageDetailsPanel';
import DocumentUploadPanel from '../service-cases/ui-elements/DocumentUploadPanel';
import CommissioningACEPartnerPanel from '../service-assignments/ui-elements/CommissioningACEPartnerPanel';
import * as saosActionTypes from './saosActionTypes';
import config from '../config';
import isACEPartnerFromDACH from '../service-assignments/isACEPartnerFromDACH';

const SAOSCostCoverageTab = props => {
    const {cx} = useStyles();
    const {translate} = useTranslate();
    const {serviceCase, serviceAssignment, users, submitSAOSCostCoverageForm} = props;
    const {submitSAOSCostCoverageServiceProviderForm, submitCreateSAOSPDF} = props;
    const {hasToResetAttachmentSelection, storeHasToResetAttachmentSelection} = props;
    const [selfPayer, setSelfPayer] = useState({
        hasSelfPayerOption: serviceCase?.nonAceMember && serviceCase?.prefix === `${config.ACE_COMMISSIONER_ID}`
            ? true : serviceAssignment.selfPayerOption,
        selfPayerNote: serviceAssignment.selfPayerNote,
    });
    const [maximumBudget, setMaximumBudget] = useState(serviceCase?.maximumBudget || '');
    const [additionalInformationText, setAdditionalInformationText] = useState(
        serviceAssignment.additionalInformationText,
    );
    const [description, setDescription] = useState(serviceAssignment.description);
    const descriptionRef = useRef(description);
    const [totalCost, setTotalCost] = useState(serviceAssignment.totalCost);
    const [callbackPhoneNo, setCallbackPhoneNo] = useState(serviceAssignment.callbackPhoneNo || '');
    const [fallbackSystemId, setFallbackSystemId] = useState(serviceAssignment?.fallbackSystemId || '');
    const [isFallbackSystemIdEnabled, setIsFallbackSystemIdEnabled] = useState(
        !!serviceAssignment?.isFallbackSystemIdEnabled,
    );
    const [attachmentsToSendWithAssignment, setAttachmentsToSendWithAssignment] = useState([]);

    useEffect(() => {
        if (!hasToResetAttachmentSelection) return;
        setAttachmentsToSendWithAssignment([]);
        storeHasToResetAttachmentSelection({hasToResetAttachmentSelection: false});
    }, [hasToResetAttachmentSelection, storeHasToResetAttachmentSelection, setAttachmentsToSendWithAssignment]);

    useEffect(() => {
        setAdditionalInformationText(serviceAssignment.additionalInformationText);
        setCallbackPhoneNo(serviceAssignment.callbackPhoneNo);
    }, [serviceAssignment.additionalInformationText, serviceAssignment.callbackPhoneNo]);

    useEffect(() => {
        if (serviceAssignment.description !== descriptionRef.current) {
            descriptionRef.current = serviceAssignment.description;
            setDescription(serviceAssignment.description);
        }
    }, [serviceAssignment.description, descriptionRef, setDescription]);

    const onChangeHandler = (key, value) => {
        if (key === 'hasSelfPayerOption') {
            setSelfPayer({
                ...selfPayer,
                [key]: value,
                selfPayerNote: '',
            });
            return;
        }

        if (key === 'selfPayerNote' && value.length <= config.MAXIMUM_SELF_PAYER_NOTE_LENGTH) {
            setSelfPayer({
                ...selfPayer,
                [key]: value,
            });
            return;
        }

        if (key === 'additionalInformationText' && value.length <= config.MAXIMUM_TEXT_AREA_CONTENT_LENGTH) {
            setAdditionalInformationText(value);
            return;
        }

        if (key === 'callbackPhoneNo') {
            setCallbackPhoneNo(value);
            return;
        }

        if (key === 'fallbackSystemId') {
            setFallbackSystemId(value);
            return;
        }

        if (key === 'isFallbackSystemIdEnabled') {
            setIsFallbackSystemIdEnabled(value);
            setFallbackSystemId('');
            return;
        }

        if (key === 'description') {
            setDescription(value);
            return;
        }

        if (key === 'totalCost') {
            setTotalCost(value);
        }

        if (key === 'maximumBudget') {
            setMaximumBudget(value);
        }
    };

    useRouteUnmountEffect(({completeRouteUnmountSideEffect}) => {
        if (!selfPayer) {
            completeRouteUnmountSideEffect({
                caller: saosActionTypes.SUBMIT_SAOS_COST_COVERAGE_FORM,
            });
            return;
        }

        submitSAOSCostCoverageForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData: {
                selfPayerOption: selfPayer.hasSelfPayerOption,
                selfPayerNote: selfPayer.selfPayerNote,
                additionalInformationText,
                description,
                totalCost,
                callbackPhoneNo,
                fallbackSystemId,
                isFallbackSystemIdEnabled,
            },
            serviceCaseData: {
                maximumBudget,
            },
            nonAceMember: serviceCase.nonAceMember,
            attachmentsToSendWithAssignment,
        });
    }, [
        selfPayer,
        serviceAssignment.lineNo,
        serviceAssignment.serviceCaseId,
        additionalInformationText,
        description,
        totalCost,
        callbackPhoneNo,
        fallbackSystemId,
        isFallbackSystemIdEnabled,
        submitSAOSCostCoverageForm,
        maximumBudget,
        serviceCase.nonAceMember,
        attachmentsToSendWithAssignment,
    ]);

    const handleOnSend = costCoverageData => {
        const serviceAssignmentData = {
            selfPayerOption: selfPayer.hasSelfPayerOption,
            selfPayerNote: selfPayer.selfPayerNote,
            additionalInformationText,
            description,
            totalCost,
            callbackPhoneNo,
            fallbackSystemId,
            isFallbackSystemIdEnabled,
            ...costCoverageData,
        };

        const serviceCaseData = {
            maximumBudget,
        };

        submitSAOSCostCoverageServiceProviderForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData,
            serviceCaseData,
            assignmentType: efServiceAssignmentTypes.OTHER_SERVICES,
            serviceCaseType: serviceCase.caseType,
            attachmentsToSendWithAssignment,
        });

        // on send, reset attachments state: see https://computerrock.atlassian.net/browse/ACEECS-5792
        setAttachmentsToSendWithAssignment([]);
    };

    const submitCreatePDF = costCoverageData => {
        const serviceAssignmentData = {
            selfPayerOption: selfPayer.hasSelfPayerOption,
            selfPayerNote: selfPayer.selfPayerNote,
            additionalInformationText,
            description,
            totalCost,
            callbackPhoneNo,
            fallbackSystemId,
            isFallbackSystemIdEnabled,
            ...costCoverageData,
        };

        const serviceCaseData = {
            maximumBudget,
        };

        const isDACH = isACEPartnerFromDACH(serviceAssignment?.acePartner);

        submitCreateSAOSPDF({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData,
            serviceCaseData,
            serviceAssignmentType: efServiceAssignmentTypes.OTHER_SERVICES,
            serviceCaseType: serviceCase.caseType,
            attachmentsToSendWithAssignment,
            prefix: serviceCase.prefix,
            serviceType: isDACH
                ? translate(`global.service_type.${serviceAssignment.assignmentType.toLowerCase()}`)
                : serviceAssignment.assignmentType.toLowerCase(),
        });
    };

    // if no case or assignment don't render
    if (!serviceCase || !serviceAssignment) return null;

    return (
        <ContentBlock>
            <ContentItem className={cx('ace-c-content-item--span-9')}>
                <CommissioningBasicDataPanel
                    selfPayer={selfPayer}
                    maximumBudget={maximumBudget}
                    agentUserProfile={users[serviceAssignment.createdBy]}
                    additionalInformationText={additionalInformationText}
                    onChangeHandler={onChangeHandler}
                    callbackPhoneNo={callbackPhoneNo}
                    fallbackSystemId={fallbackSystemId}
                    isFallbackSystemIdEnabled={isFallbackSystemIdEnabled}
                />
                <CostCoverageCostCoveragePanel
                    description={description}
                    totalCost={totalCost}
                    onChangeHandler={onChangeHandler}
                />
                <CostCoverageDetailsPanel serviceCase={serviceCase} />
            </ContentItem>
            <ContentItem className={cx('ace-c-content-item--span-3')}>
                <CommissioningACEPartnerPanel
                    assignmentChannels={[
                        apmContractPartnerAssignmentChannelTypes.EMAIL_PDF_XML,
                        apmContractPartnerAssignmentChannelTypes.FAX,
                    ]}
                    handleOnAssignmentSubmit={handleOnSend}
                    attachmentsToSendWithAssignment={attachmentsToSendWithAssignment}
                    submitCreatePDFForm={submitCreatePDF}
                />
                {!config.IS_FALLBACK_SYSTEM && (
                    <DocumentUploadPanel
                        attachmentsToSendWithAssignment={attachmentsToSendWithAssignment}
                        setAttachmentsToSendWithAssignment={setAttachmentsToSendWithAssignment}
                    />
                )}
            </ContentItem>
        </ContentBlock>
    );
};

SAOSCostCoverageTab.propTypes = {
    submitSAOSCostCoverageForm: PropTypes.func.isRequired,
    serviceCase: PropTypes.object,
    serviceAssignment: PropTypes.object,
    users: PropTypes.object,
    submitSAOSCostCoverageServiceProviderForm: PropTypes.func.isRequired,
    submitCreateSAOSPDF: PropTypes.func.isRequired,
    hasToResetAttachmentSelection: PropTypes.bool,
    storeHasToResetAttachmentSelection: PropTypes.func.isRequired,
};

SAOSCostCoverageTab.defaultProps = {
    serviceCase: null,
    serviceAssignment: {},
    users: {},
    hasToResetAttachmentSelection: false,
};

const mapStateToProps = (state, props) => {
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    return {
        serviceCase: serviceCaseSelector(state, props),
        serviceAssignment: serviceAssignmentSelector(state, props),
        users: userProfileSelectors.getUsers(state),
        hasToResetAttachmentSelection: state.serviceAssignments.hasToResetAttachmentSelection,
    };
};

const mapDispatchToProps = dispatch => ({
    submitSAOSCostCoverageForm: payload => dispatch({
        type: saosActionTypes.SUBMIT_SAOS_COST_COVERAGE_FORM,
        payload,
    }),
    submitSAOSCostCoverageServiceProviderForm: payload => dispatch({
        type: serviceAssignmentActionTypes.SUBMIT_COMMISSIONING_ACE_PARTNER_FORM,
        payload,
    }),
    submitCreateSAOSPDF: payload => dispatch({
        type: serviceAssignmentActionTypes.CREATE_SERVICE_ASSIGNMENT_PDF,
        payload,
    }),
    storeHasToResetAttachmentSelection: payload => dispatch({
        type: serviceAssignmentActionTypes.STORE_HAS_TO_RESET_ASSIGNMENT_SELECTION,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SAOSCostCoverageTab));
